import { TZDate } from '@date-fns/tz';
import {
  GarageScheduleDayRecord,
  GarageScheduleRecord,
  ResourceStatus,
  WorkstationSlotStatus,
  type GarageScheduleWorkstationRecord
} from '@navio/nsdk';
import { decimalHourToClock, differenceBetweenDates, isSameDay, NoddiDateFormats, noddiFormatDate } from '../date/date';
import { secondsToTimeString } from '../utils/secondsToTimeString';
import { buildHourPressure } from './shopAvailabilityHours';
import {
  ShopAvailabilityDay,
  ShopAvailabilityWeek,
  StationBooking,
  StationLoad,
  StationStatus,
  TimeSpan
} from './shopAvailabilityTypes';

/** A shop's day, paired with the time zone its UTC datetimes must be read in. */
type ShopDayRecord = {
  day: GarageScheduleDayRecord;
  shopId: number;
  shopName: string;
  timeZone: string;
};

const MINUTES_PER_HOUR = 60;

/** Slot statuses that take a station out of play. BLOCKED is downtime, not booking pressure. */
const BUSY_SLOT_STATUSES: WorkstationSlotStatus[] = [
  WorkstationSlotStatus.BOOKED,
  WorkstationSlotStatus.BOOKING_EXTERNAL,
  WorkstationSlotStatus.RESERVED
];

/** Slots that are a customer booking — reservations hold time but are not booked jobs. */
const BOOKING_SLOT_STATUSES: WorkstationSlotStatus[] = [
  WorkstationSlotStatus.BOOKED,
  WorkstationSlotStatus.BOOKING_EXTERNAL
];

export const buildShopAvailabilityWeek = ({
  schedules,
  weekDates,
  stationId
}: {
  schedules: GarageScheduleRecord[];
  weekDates: Date[];
  /** Narrow every figure to one workstation. The breakdown still lists them all. */
  stationId?: number;
}): ShopAvailabilityWeek => {
  const days = weekDates.map((date) => buildDay(date, collectShopDays(schedules, date), stationId));

  return {
    days,
    bookingCount: sumBy(days, (day) => day.bookingCount),
    carCount: sumBy(days, (day) => day.carCount),
    bookedSeconds: sumBy(days, (day) => day.bookedSeconds),
    bookableSeconds: sumBy(days, (day) => day.bookableSeconds),
    // Each shop's own bay count (stable across its days), summed over the shops in scope.
    workstationCount: sumBy(schedules, (schedule) =>
      Math.max(0, ...schedule.days.map((day) => activeWorkstations(day).length))
    )
  };
};

/**
 * The day a shop load view shows. A picked day wins outright — a stepper walks calendar days, so landing on a
 * closed one shows it as closed. Without a pick, today stands in, or the nearest open day if it is closed.
 */
export const resolveShopAvailabilityDay = (days: ShopAvailabilityDay[], pickedDate: Date | null) => {
  if (pickedDate) {
    return days.find((day) => isSameDay(day.date, pickedDate)) ?? nearestOpenDay(days, pickedDate);
  }
  const today = days.find((day) => isSameDay(day.date, new Date()));
  return today && !today.isClosed ? today : nearestOpenDay(days, new Date());
};

/** Open day closest to `date`, preferring the later one when two are equally close. */
const nearestOpenDay = (days: ShopAvailabilityDay[], date: Date) =>
  days
    .filter((day) => !day.isClosed)
    .sort(
      (a, b) =>
        Math.abs(differenceBetweenDates(a.date, date, 'days')) -
          Math.abs(differenceBetweenDates(b.date, date, 'days')) || differenceBetweenDates(b.date, a.date, 'days')
    )
    .at(0) ?? days[0];

const collectShopDays = (schedules: GarageScheduleRecord[], date: Date): ShopDayRecord[] => {
  const dateString = noddiFormatDate(date, NoddiDateFormats.API_DATE);
  return schedules.flatMap((schedule) => {
    const day = schedule.days.find((scheduleDay) => scheduleDay.date === dateString);
    return day
      ? [{ day, shopId: schedule.garage.id, shopName: schedule.garage.name, timeZone: schedule.timeZone }]
      : [];
  });
};

const buildDay = (date: Date, shopDays: ShopDayRecord[], stationId?: number): ShopAvailabilityDay => {
  const openShopDays = shopDays.filter((shopDay) => !shopDay.day.isClosed);
  // Booked and bookable time per bay, so every figure — day, hour and station row — reads
  // the same source, and narrowing to one workstation needs no separate arithmetic.
  const inScope = openShopDays
    .flatMap((shopDay) =>
      stationsInPlay(shopDay.day).map((workstation) => ({
        id: workstation.workstationId,
        booked: occupiedSpans(workstation, shopDay.timeZone),
        bookable: bookableSpans(workstation, shopDay.timeZone),
        staffed: staffedSpans(workstation, shopDay.timeZone),
        bookingCount: countBookings(workstation),
        carCount: countCars(workstation)
      }))
    )
    .filter((station) => stationId === undefined || station.id === stationId);

  const bookedSeconds = sumBy(inScope, (station) => spanSeconds(station.booked));
  const bookableSeconds = sumBy(inScope, (station) => spanSeconds(station.bookable));
  // The badge stays on staffed time. A bay filter uses that bay's shift, not its bookings.
  const shopWindow = {
    from: minOrNull(
      openShopDays.map((shopDay) => toDecimalHour(shopDay.day.openStartsAt, shopDay.timeZone)).filter(isNumber)
    ),
    to: maxOrNull(
      openShopDays.map((shopDay) => toDecimalHour(shopDay.day.openEndsAt, shopDay.timeZone)).filter(isNumber)
    )
  };
  const staffedSpansInScope = inScope.flatMap((station) => station.staffed);
  const bookedSpansInScope = inScope.flatMap((station) => station.booked);
  const openFromHour =
    stationId === undefined ? shopWindow.from : minOrNull(staffedSpansInScope.map((span) => span.start));
  const openToHour = stationId === undefined ? shopWindow.to : maxOrNull(staffedSpansInScope.map((span) => span.end));
  // Bookings made before the shop was closed still belong on the chart.
  const chartFromHour = earliest(openFromHour, minOrNull(bookedSpansInScope.map((span) => span.start)));
  const chartToHour = latest(openToHour, maxOrNull(bookedSpansInScope.map((span) => span.end)));
  const openingWindow: TimeSpan | null =
    openFromHour !== null && openToHour !== null ? { start: openFromHour, end: openToHour } : null;

  return {
    date,
    isClosed: inScope.length === 0,
    bookingCount: sumBy(inScope, (station) => station.bookingCount),
    carCount: sumBy(inScope, (station) => station.carCount),
    bookedSeconds,
    bookableSeconds,
    bookedPercent: toPercent(bookedSeconds, bookedSeconds + bookableSeconds),
    openFromHour,
    openToHour,
    outsideBookedSeconds: sumBy(inScope, (station) => secondsOutside(station.booked, openingWindow)),
    hours: buildHourPressure({
      stations: inScope,
      openFromHour,
      openToHour,
      chartFromHour,
      chartToHour
    }),
    stations: buildStationLoads(shopDays)
  };
};

/** Every bay the shop has that day — closed ones included, so the list is the whole shop. */
const buildStationLoads = (shopDays: ShopDayRecord[]): StationLoad[] => {
  const showShopName = shopDays.length > 1;

  return shopDays
    .flatMap((shopDay) =>
      shopDay.day.workstations.map((workstation) => {
        const workSpans = mergeSpans(staffedSpans(workstation, shopDay.timeZone));
        // Bookable time comes from the same free windows the chart uses, so a row, its hours
        // and its drill can never disagree.
        const freeSpans = bookableSpans(workstation, shopDay.timeZone);
        const bookings = stationBookings(workstation, shopDay.timeZone);
        const bookedSeconds = spanSeconds(occupiedSpans(workstation, shopDay.timeZone));
        const bookableSeconds = spanSeconds(freeSpans);
        return {
          id: workstation.workstationId,
          name: workstation.name.trim(),
          shopId: shopDay.shopId,
          shopName: showShopName ? shopDay.shopName : null,
          status: getStationStatus(workstation, shopDay.day.isClosed),
          shiftLabel: getSpanRangeLabel(workSpans),
          workSpans,
          breaks: gapsBetween(workSpans),
          freeSpans,
          bookings,
          bookedSeconds,
          bookableSeconds,
          bookedPercent: toPercent(bookedSeconds, bookedSeconds + bookableSeconds),
          bookingCount: countBookings(workstation),
          carCount: countCars(workstation)
        };
      })
    )
    .sort(
      (a, b) =>
        (a.shopName ?? '').localeCompare(b.shopName ?? '') ||
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    );
};

const getStationStatus = (workstation: GarageScheduleWorkstationRecord, isShopClosed: boolean): StationStatus => {
  if (workstation.status === ResourceStatus.OUT_OF_ORDER) {
    return 'outOfOrder';
  }
  return !isShopClosed && isInPlay(workstation) ? 'open' : 'closed';
};

const countBookings = (workstation: GarageScheduleWorkstationRecord) =>
  workstation.slots.filter((slot) => BOOKING_SLOT_STATUSES.includes(slot.status)).length;

// An external booking carries no cars; its slot still holds one.
const countCars = (workstation: GarageScheduleWorkstationRecord) =>
  sumBy(
    workstation.slots.filter((slot) => BOOKING_SLOT_STATUSES.includes(slot.status)),
    (slot) => Math.max(1, slot.booking?.cars.length ?? 0)
  );

/** "07:00–15:00" across every span, or empty when there are none. */
export const getSpanRangeLabel = (spans: TimeSpan[]) => {
  if (spans.length === 0) {
    return '';
  }
  return formatSpan({
    start: Math.min(...spans.map((span) => span.start)),
    end: Math.max(...spans.map((span) => span.end))
  });
};

export const formatSpan = (span: TimeSpan) => `${decimalHourToClock(span.start)}–${decimalHourToClock(span.end)}`;

export const spanSeconds = (spans: TimeSpan[]) =>
  spans.reduce((seconds, span) => seconds + (span.end - span.start) * MINUTES_PER_HOUR * 60, 0);

/** Seconds of `spans` that fall outside `window`. With no window, the whole span is outside. */
const secondsOutside = (spans: TimeSpan[], window: TimeSpan | null) =>
  spans.reduce((seconds, span) => {
    const overlap = window ? Math.max(0, Math.min(span.end, window.end) - Math.max(span.start, window.start)) : 0;
    return seconds + (span.end - span.start - overlap) * MINUTES_PER_HOUR * 60;
  }, 0);

/** Overlapping or touching spans merged into one, ordered by start. */
export const mergeSpans = (spans: TimeSpan[]): TimeSpan[] =>
  [...spans]
    .sort((a, b) => a.start - b.start)
    .reduce<TimeSpan[]>((merged, span) => {
      const last = merged.at(-1);
      if (last && span.start <= last.end) {
        return [...merged.slice(0, -1), { start: last.start, end: Math.max(last.end, span.end) }];
      }
      return [...merged, span];
    }, []);

/** The holes between consecutive spans — a shift's breaks, or a bay's free gaps. */
const gapsBetween = (spans: TimeSpan[]): TimeSpan[] =>
  spans.slice(1).flatMap((span, index) => {
    const previousEnd = spans[index]?.end ?? span.start;
    return previousEnd < span.start ? [{ start: previousEnd, end: span.start }] : [];
  });

/** Part of `span` that falls inside `window`, or null when they do not overlap. */
export const clampSpan = (span: TimeSpan, window: TimeSpan): TimeSpan | null => {
  const start = Math.max(span.start, window.start);
  const end = Math.min(span.end, window.end);
  return end > start ? { start, end } : null;
};

const activeWorkstations = (day: GarageScheduleDayRecord) =>
  day.workstations.filter((workstation) => workstation.status === ResourceStatus.ACTIVE);

/**
 * The stations the day actually runs on: staffed, or occupied by a booking. Bays nobody
 * works are no part of the day's pressure — counting them would dilute every hour.
 */
const stationsInPlay = (day: GarageScheduleDayRecord) => activeWorkstations(day).filter(isInPlay);

const isInPlay = (workstation: GarageScheduleWorkstationRecord) =>
  workstation.staffing.length > 0 || workstation.slots.some((slot) => BUSY_SLOT_STATUSES.includes(slot.status));

const occupiedSpans = (workstation: GarageScheduleWorkstationRecord, timeZone: string): TimeSpan[] =>
  workstation.slots
    .filter((slot) => BUSY_SLOT_STATUSES.includes(slot.status))
    .flatMap((slot) => toSpan(slot.startsAt, slot.endsAt, timeZone));

/** Staffed time on this bay no slot has taken yet — the backend's own bookable gaps. */
const bookableSpans = (workstation: GarageScheduleWorkstationRecord, timeZone: string): TimeSpan[] =>
  workstation.freeWindows.flatMap((window) => toSpan(window.start, window.end, timeZone));

/** Every occupying slot on the bay, named for the drill views. */
const stationBookings = (workstation: GarageScheduleWorkstationRecord, timeZone: string): StationBooking[] =>
  workstation.slots
    .filter((slot) => BUSY_SLOT_STATUSES.includes(slot.status))
    .flatMap((slot, index) => {
      const [span] = toSpan(slot.startsAt, slot.endsAt, timeZone);
      if (!span) {
        return [];
      }
      const car = slot.booking?.cars[0];
      return [
        {
          key: `${workstation.workstationId}-${slot.workstationSlotId ?? index}`,
          span,
          customerName:
            slot.booking?.customerName ||
            slot.customerName ||
            (slot.status === WorkstationSlotStatus.RESERVED ? 'Reserved' : 'Booking'),
          licensePlate: car?.licensePlateNumber ?? slot.licensePlate ?? '',
          carName: car?.carName ?? '',
          serviceLabel: slot.booking?.salesItems.map((salesItem) => salesItem.name).join(', ') ?? '',
          booking: slot.booking ?? null
        }
      ];
    })
    .sort((a, b) => a.span.start - b.span.start);

/** The bay's working windows for the day — breaks are the gaps between them. */
const staffedSpans = (workstation: GarageScheduleWorkstationRecord, timeZone: string): TimeSpan[] =>
  workstation.staffing.flatMap((staffing) =>
    staffing.windows.flatMap((window) => toSpan(window.start, window.end, timeZone))
  );

const toSpan = (from: string, to: string, timeZone: string): TimeSpan[] => {
  const start = toDecimalHour(from, timeZone);
  const end = toDecimalHour(to, timeZone);
  return start === null || end === null ? [] : [{ start, end }];
};

/** The shop's wall clock, read in its own zone so every device draws the same hours. */
const toDecimalHour = (iso: string | null | undefined, timeZone: string): number | null => {
  if (!iso) {
    return null;
  }
  const local = new TZDate(iso, timeZone);
  return local.getHours() + local.getMinutes() / MINUTES_PER_HOUR;
};

/** Compact hours ("24h", "1h 30m"), never days; "0h" rather than an empty string. */
export const formatLoadDuration = (seconds: number) =>
  secondsToTimeString({
    seconds: Math.max(0, Math.round(seconds)),
    alwaysIncludeSeconds: false,
    shortFormat: true,
    hideZeroSeconds: true,
    includeDays: false
  }) || '0h';

const toPercent = (part: number, whole: number) => (whole === 0 ? 0 : Math.round((part / whole) * 100));

const sumBy = <T>(items: T[], getValue: (item: T) => number) => items.reduce((sum, item) => sum + getValue(item), 0);

const isNumber = (value: number | null): value is number => value !== null;

const minOrNull = (values: number[]) => (values.length === 0 ? null : Math.min(...values));

const maxOrNull = (values: number[]) => (values.length === 0 ? null : Math.max(...values));

const earliest = (...values: Array<number | null>) => minOrNull(values.filter(isNumber));

const latest = (...values: Array<number | null>) => maxOrNull(values.filter(isNumber));
